/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import formatKpiValue from "components/financials/modern/kpis/formatKpiValue";
import useTheme from "components/ui/useTheme";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import type Highcharts from "highcharts/highstock";
import type { SeriesAreaOptions, SeriesBarOptions } from "highcharts/highstock";
import { useRef } from "react";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import { IFinancialsStatementType } from "types/graphqlTypes";

type KeyPerformanceIndicatorDataSeries = {
  name: string;
  format: string;
  isCurrency: boolean;
  data: {
    x: number;
    y: number | null;
  }[];
};

interface KpisChartProps {
  statementType: IFinancialsStatementType;
  plotType: "area" | "column";
  currency: string;
  kpis: KeyPerformanceIndicatorDataSeries[];
}

export default function KpisChart({ statementType, plotType, currency, kpis }: KpisChartProps) {
  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);
  const theme = useTheme();
  const isDark = theme === "dark";
  const slateOrDarkSlate = isDark ? colors.slate[800] : colors.slate[100];
  const labelsColor = isDark ? colors.slate[300] : colors.slate[500];
  const intl = useIntl();
  const formattedKpisTitle = {
    metric: intl.formatMessage({ defaultMessage: "Métrica", id: "kg/wta", description: "Tipo de KPI" }),
    number: intl.formatMessage({ defaultMessage: "Número", id: "X53X0L", description: "Tipo de KPI" }),
    currency: intl.formatMessage({ defaultMessage: "$", id: "HzMRF6", description: "Tipo de KPI" }),
    percentage: intl.formatMessage({ defaultMessage: "%", id: "avDHVQ", description: "Tipo de KPI" }),
  };

  const series = kpis.map((node, index) => {
    const theme = getColorThemeForIndex(index, isDark ? "dark" : "light");

    if (!theme) {
      throw new Error("Theme not found");
    }

    const defaultOptions = {
      showInNavigator: true,
      type: plotType,
      animation: false,
      name: node.name,
      yAxis: index,
      color: theme.color,
      lineColor: theme.lineColor,
      lineWidth: 2,
      data: node.data
        .sort((a, b) => compareAsc(a.x, b.x))
        .map((point) => {
          let formatTitle = formattedKpisTitle.number;
          if (node.isCurrency) {
            formatTitle = formattedKpisTitle.currency;
          }
          if (node.format === "ratio") {
            formatTitle = formattedKpisTitle.metric;
          }
          if (node.format === "%") {
            formatTitle = formattedKpisTitle.percentage;
          }
          return {
            x: point.x,
            y: point.y,
            custom: {
              formattedDate: intl.formatDate(point.x, {
                year: "numeric",
                month: "numeric",
                day: "numeric",
              }),
              formattedValue: formatKpiValue(point.y, currency, node.format, node.isCurrency),
              formatTitle,
              primaryColor: theme.lineColor,
              secondaryColor: theme.lineColor,
            },
          } as Highcharts.PointOptionsType;
        }),
      custom: {
        primaryColor: theme.color,
        secondaryColor: theme.lineColor,
      },
      marker: {
        width: 8,
        height: 8,
        lineWidth: 1,
        fillColor: theme.color,
        color: theme.color,
        lineColor: theme.lineColor,
        symbol: "circle",
      },
    };

    if (plotType === "column") {
      const columnOptions = {
        borderColor: theme.lineColor,
        borderWidth: 1.5,
        states: {
          hover: {
            color: theme.lineColor,
          },
        },
      };
      return {
        ...columnOptions,
        ...defaultOptions,
      };
    }

    return defaultOptions;
  }) as (SeriesAreaOptions | SeriesBarOptions)[];

  const options: Highcharts.Options = {
    time: {
      timezone: "UTC",
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series,
    legend: {
      align: "left",
      enabled: true,
      padding: 4,
      alignColumns: true,
      itemDistance: 16,
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: labelsColor,
        fontFamily: aeonik.style.fontFamily,
        fontWeight: "500",
        fontSize: "12px",
        width: 240,
        textOverflow: "ellipsis",
      },
      reversed: true,
    },
    xAxis: {
      type: "datetime",
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickColor: slateOrDarkSlate,
      tickWidth: 1,
      scrollbar: {
        enabled: false,
      },
      // https://stackoverflow.com/a/68760681
      tickPositioner: function () {
        // @ts-ignore
        if (Array.isArray(this.series) && this.series.length > 0 && this.series[0].xData) {
          // @ts-ignore
          return this.series[0].xData;
        }
        return undefined;
      },
      showFirstLabel: true,
      showLastLabel: true,
      labels: {
        step: 1,
        enabled: true,
        formatter: function () {
          // @ts-ignore
          return intl.formatDate(this.value, {
            year: statementType === IFinancialsStatementType.Annual ? "numeric" : "2-digit",
            month: "numeric",
          });
        },
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    yAxis: kpis.map((_, index) => {
      // const minValues = node.data.map((node) => node.y);
      // const minValue = minValues.length > 0 ? Math.min(...minValues.filter(notUndefinedOrNull)) : 0;

      return {
        type: "linear",
        min: 0,
        gridLineColor: slateOrDarkSlate,
        lineWidth: 1,
        lineColor: slateOrDarkSlate,
        labels: {
          enabled: true,
          align: "center",
          style: {
            color: labelsColor,
            fontFamily: aeonik.style.fontFamily,
            fontWeight: "500",
            fontSize: "12px",
          },
          formatter: function () {
            const valueFormatter = new Intl.NumberFormat("en", {
              notation: "compact",
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
            });
            return valueFormatter.format(parseFloat(String(this.value)));
          },
        },
        tickWidth: 0,
        showFirstLabel: true,
        showLastLabel: false,
        scrollbar: {
          enabled: false,
        },
        opposite: !!index,
      };
    }),
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      style: {
        color: labelsColor,
      },
      borderColor: slateOrDarkSlate,
      backgroundColor: isDark ? colors.slate[800] : colors.white,
      borderWidth: 1,
      padding: 12,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      headerFormat: '<span style="font-size: 0.8em">{point.custom.formattedDate}</span><br/>',
      pointFormat:
        '<span style="color: {point.custom.primaryColor}">\u25CF</span> {series.name} ({point.custom.formatTitle}): <b>{point.custom.formattedValue}</b><br/>',
    },
    plotOptions: {
      column: {
        grouping: true,
        dataLabels: {
          enabled: true,
          crop: false,
          style: {
            color: isDark ? colors.slate[300] : colors.slate[800],
            fontFamily: aeonik.style.fontFamily,
            fontWeight: "600",
            fontSize: "10px",
            padding: "8px",
          },
          formatter() {
            return (this.point as any).custom.formattedValue || "";
          },
        },
      },
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: colors.slate[500],
            color: colors.slate[50],
            lineWidth: 2,
          },
        },
      },
      area: {
        color: colors.teal[50],
        lineColor: colors.teal[600],
        lineWidth: 2,
        marker: {
          enabled: false,
          fillColor: colors.teal[50],
          lineColor: colors.teal[600],
          lineWidth: 4,
          width: 8,
          height: 8,
        },
        dataLabels: {
          enabled: true,
          crop: false,
          style: {
            color: isDark ? colors.slate[300] : colors.slate[800],
            fontFamily: aeonik.style.fontFamily,
            fontWeight: "600",
            fontSize: "10px",
            padding: "8px",
          },
          formatter() {
            return (this.point as any).custom.formattedValue || "";
          },
        },
      },
      bar: {
        borderWidth: 1.5,
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };

  return (
    <div className="flex h-full w-full flex-col space-y-4">
      <HighchartsChart ref={chartRef} options={options} className="h-96 xl:h-full" />
    </div>
  );
}
